"use client"

import * as React from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useAuth } from "@/lib/use-auth"
import { ThemeToggle } from "@/components/theme-toggle"
import { getAvatarInitials } from "@/lib/utils"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"

// Icons used by the TopBar user dropdown menu. The NAV_ITEMS icons live
// in `../nav-config.ts` (preserved VERBATIM with their original import-block
// comments).
import { User, Settings, LogOut } from "lucide-react"

// ---------------------------------------------------------------------------
// TopBar — the header area above the main content
// ---------------------------------------------------------------------------

export function TopBar() {
  // AUDIT-006 fix (FASE 3 C1): eliminado `usePathname()` — ya no se usa en TopBar
  // (el breadcrumb fue removido). Si se restaura el breadcrumb, volver a añadir.
  const router = useRouter()
  const { user, logout } = useAuth()

  // AUDIT-006 fix (FASE 3 C1): eliminado `const crumbs = generateBreadcrumbs(pathname)`
  // — el breadcrumb del TopBar fue removido, ya no se necesita esta variable.

  // P-109 (SPEC-090 §1.6 — session expired UX): same fix as the
  // AppSidebar — drop the "Usuario" / "Gratuito" / "U" fallbacks so
  // the brief pre-redirect window (after useAuth sets user=null +
  // before the AppShell's session-expired handler completes the
  // `router.replace('/login?reason=session_expired')`) doesn't
  // display the misleading "U Usuario" state in the TopBar avatar
  // dropdown either.
  const displayName = user?.profile?.name || user?.username || ""
  const avatar = user?.profile?.avatar || undefined
  const email = user?.email ?? ""

  // P-UX-013 (SPEC-165): standardized avatar initials via shared helper.
  const initials = getAvatarInitials(displayName, user?.username ?? "")

  // H-04 fix: removed redundant router.replace('/login') — logout()
  // already redirects to /login?reason=logout, so the explicit
  // router.replace was overriding the ?reason query param.
  const onLogout = React.useCallback(async () => {
    await logout()
  }, [logout])

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border/50 bg-background/80 px-4 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
      {/* Mobile sidebar trigger */}
      <SidebarTrigger className="-ml-1 mr-1" />

      <Separator orientation="vertical" className="mr-2 hidden h-4 md:flex" />

      {/* AUDIT-006 fix (FASE 3 C1): eliminado el breadcrumb del TopBar del AppShell.
          Cada página /app/* tiene su propio <PageBreadcrumbs> con labels más
          específicos y legibles (ej: "Inicio > Billetera" en lugar de "App > wallet").
          Antes se mostraban DOS breadcrumbs simultáneamente: el del AppShell (genérico,
          basado en pathname) + el de la página (específico, hardcodeado). El del
          AppShell era redundante y duplicaba información. Las páginas que NO tienen
          PageBreadcrumbs (como /app/conferences/[roomId]) muestran solo el título H1
          en el header, que es suficiente contexto para el usuario. */}

      {/* Spacer */}
      <div className="flex-1" />

      {/* fix(spec-045-A4-LOW-004): removed the permanently-disabled search
          input (was dead UI — `disabled title="Próximamente"` with no
          implementation timeline visible to the user). When search is
          implemented in a future sprint, restore a wired-up input here. */}

      {/* fix(spec-045-A4-LOW-005): removed the notifications dropdown
          (was hardcoded "Sin notificaciones" with no backend API wired
          up — misleading UI that suggested the bell did something).
          When a real /api/notifications endpoint exists, restore the
          bell + dropdown here with live data. */}

      {/* Theme toggle */}
      <ThemeToggle className="size-8" />

      {/* User avatar dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="relative inline-flex items-center gap-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label="Menú de usuario"
          >
            <Avatar className="size-7 border border-border/50">
              <AvatarImage src={avatar} alt={displayName} />
              <AvatarFallback className="bg-[#2563EB]/10 text-[10px] font-semibold text-[#2563EB] dark:bg-[#2563EB]/20 dark:text-[#60A5FA]">
                {initials}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground">{email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/app/profile" className="cursor-pointer">
              <User className="mr-2 size-4" />
              Profileia Hub
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/app/settings" className="cursor-pointer">
              <Settings className="mr-2 size-4" />
              Configuración
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer text-destructive focus:text-destructive"
            onClick={onLogout}
          >
            <LogOut className="mr-2 size-4" />
            Cerrar Sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
